/* eslint-disable no-unused-vars */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { updateMenuItemsCount } from '../menu/menuSlice';
import { toast } from 'react-toastify';
import { toastConfig } from 'config';
import { userLoggedIn, userLoggedOut } from '../auth/authSlice';
import io from 'socket.io-client';
import { Mutex } from 'async-mutex';
import { API_PORT, DEPLOYENVIRONMENT, PRESENCE_PORT } from 'apiConfig';

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
	baseUrl: `https://${DEPLOYENVIRONMENT}api.garlicerp.com:${API_PORT}`,
	prepareHeaders: (headers, { getState }) => {
		const token = getState()?.auth?.accessToken;
		if (token) {
			headers.set('Authorization', `Bearer ${token}`);
			headers.set('token', `Bearer ${token}`);
		}
		return headers;
	},
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
	const socket = io(`https://${DEPLOYENVIRONMENT}socket.lynorg.com:${PRESENCE_PORT}`, { transports: ['websocket', 'webtransport'] });
	const myState = await api.getState();
	await mutex.waitForUnlock();

	let result = await baseQuery(args, api, extraOptions);

	const accessToken = myState?.auth?.accessToken;
	let refresh_token = myState?.auth.refreshToken;

	if (result?.error?.status === 403) {
		toast.error("You don't have permission to call the api. Contacts with support.", toastConfig);
	} else if (result?.error?.status === 401) {
		if (!mutex.isLocked()) {
			const release = await mutex.acquire();

			try {
				const refreshResult = await baseQuery(
					{
						url: '/authentication-service/endpoint/oauth/createRefreshToken',
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: {
							token: refresh_token,
						},
					},
					api,
					extraOptions
				);

				if (refreshResult?.data?.result?.code === '201') {
					const user = api.getState().auth;
					// store the new token
					localStorage.setItem('token', refreshResult.data.result.authResponse.id_token);
					localStorage.setItem('refreshToken', refreshResult.data.result.authResponse.refresh_token);
					// update the store with the new token
					api.dispatch(
						userLoggedIn({
							accessToken: refreshResult.data.result.authResponse.id_token,
							refreshToken: refreshResult.data.result.authResponse.refresh_token,
							roleId: user?.roleId,
							userId: user?.userId,
							roleName: user?.roleName,
						})
					);
					// retry the original query with new access token
					result = await baseQuery(args, api, extraOptions);
				} else {
					toast.error('Session expired. Please login again!.', toastConfig);
					socket.emit('user_out', { accessToken });
					localStorage.clear();
					api.dispatch(userLoggedOut());
				}
			} finally {
				release();
			}
		} else {
			await mutex.waitForUnlock();
			result = await baseQuery(args, api, extraOptions);
		}
	}

	return result;
};

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	tagTypes: ['selfAuth', 'templateForm', 'comptrollerProperty'],
	endpoints: (builder) => ({
		getPageList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-workflow-instance-stage_by-filter-v2',
				method: 'POST',
				body: data,
			}),
		}),
		getInboxCardList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-workflow-instance-cards_by-filter-v2',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => {
					return response.status === 200 && result?.status === 'SUCCESS';
				},
			}),
			async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
				try {
					const res = await queryFulfilled;
					const { menu } = getState();

					const inboxMenuData = menu?.menuNotification?.['inbox'];

					if (res?.data?.status === 'SUCCESS' && inboxMenuData?.status === 'NEW') {
						dispatch(
							updateMenuItemsCount({
								page: 'inbox',
								data: {
									...inboxMenuData,
									status: 'SEEN',
								},
							})
						);
					}
				} catch (error) {
					// console.log(error);
				}
			},
			transformResponse: (response) => {
				return {
					...response,
					imageIds: response?.lynStageListing?.map((item) => item.creatorEntityCode) || [],
				};
			},
		}),
		GetDelegationCardList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-workflow-instance-cards_by-filter-v2',
				method: 'POST',
				body: data,
			}),
			async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
				try {
					const res = await queryFulfilled;
					const { menu } = getState();

					const delegationMenuData = menu?.menuNotification?.['delegation'];

					if (res?.data?.status === 'SUCCESS' && delegationMenuData?.status === 'NEW') {
						dispatch(
							updateMenuItemsCount({
								page: 'delegation',
								data: {
									...delegationMenuData,
									count: res?.data?.totalCount,
									status: 'SEEN',
								},
							})
						);
					}
				} catch (error) {
					// console.log(error);
				}
			},
			transformResponse: (response) => {
				return {
					...response,
					imageIds: response?.lynStageListing?.map((item) => item.creatorEntityCode) || [],
				};
			},
		}),
		getIdentityInboxCardList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-identity-inbox-v2',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => {
					return response.status === 200 && result?.status === 'SUCCESS';
				},
			}),
			async onQueryStarted(arg, { queryFulfilled, dispatch, getState }) {
				try {
					const res = await queryFulfilled;
					const { menu } = getState();

					const identityMenuData = menu?.menuNotification?.['identity'];

					if (res?.data?.status === 'SUCCESS') {
						dispatch(
							updateMenuItemsCount({
								page: 'identity',
								data: {
									...identityMenuData,
									count: res?.data?.totalCount,
									status: 'SEEN',
								},
							})
						);
					}
				} catch (error) {
					// console.log(error);
				}
			},
		}),
		getUserHistory: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-case-people-profile-photos',
				method: 'POST',
				body: data,
			}),
		}),
		saveForm: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/lynform-save',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['templateForm'],
		}),
		saveJSFile: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/lynform-save-js',
				method: 'POST',
				body: data,
			}),
		}),
		getForm: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/lynform-get',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 && result?.status === 'SUCCESS',
			}),
		}),
		SearchMember: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/entity-no-pic/chat-users-list',
				method: 'POST',
				body: data,
			}),
		}),
		GetRecentConversation: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/query-v3',
				method: 'POST',
				body: data,
			}),
		}),
		GetLeaseConsoleData: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/query-v5',
				method: 'POST',
				body: data,
			}),
		}),
		getImageListByUserIds: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/entity/get-profile-pic',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 && result?.status === 'SUCCESS',
			}),
		}),
		getImageById: builder.query({
			query: ({ userId, size }) => ({
				url: `/lynorg-service/endpoint/entity/get-profile-pic?userId=${userId}&size=${size}`,
				method: 'GET',
				validateStatus: (response, result) => response.status === 200 && result?.status === 'SUCCESS',
			}),
		}),
		getProfilePic: builder.query({
			query: ({ size }) => ({
				url: `/lynorg-service/endpoint/entity/get-slf-profile-pic?size=${size}`,
				method: 'GET',
				validateStatus: (response, result) => response.status === 200 && result?.status === 'SUCCESS',
			}),
		}),
		GetOnetoOneChatCounts: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/personal-chat/get-slf-total-unread-personal-chat-count',
				method: 'POST',
				body: data,
			}),
		}),
		GetTaskConversationUserList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/lightwork/get-cmp-chat-people',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 && result?.status === 'success',
			}),
			transformResponse: (response) => {
				let userObjList = {};

				response?.committeePeopleList?.forEach((item) => {
					const userDetails = item?.user;

					userObjList[userDetails?.entityCode] = userDetails;
				});

				return {
					...response,
					committeeList: userObjList,
				};
			},
		}),
		GetCascadeConversationUserList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/cascade/get-cmp-chat-people',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 && result?.status === 'success',
			}),
			transformResponse: (response) => {
				let userObjList = {};

				response?.committeePeopleList?.forEach((item) => {
					const userDetails = item?.user;

					userObjList[userDetails?.entityCode] = userDetails;
				});

				return {
					...response,
					committeeList: userObjList,
				};
			},
		}),
		MarkSeenMessageCascade: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/cascade-chat/mark-seen-single',
				method: 'POST',
				body: data,
			}),
		}),
		MarkSeenMessageCascadeV3: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/cascade-chat-v3/mark-seen-single',
				method: 'POST',
				body: data,
			}),
		}),
		MarkSeenMessageWorkflow: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/message/mark-seen-single',
				method: 'POST',
				body: data,
			}),
		}),
		MarkSeenMessageOneToOne: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/personal-chat/mark-seen-single',
				method: 'POST',
				body: data,
			}),
		}),
		sendControlData: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/external/lyntask',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 || result?.status === 'SUCCESS',
			}),
		}),
		DynamicReportList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/report-spec/get-by-module',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 || result?.status === 'SUCCESS',
			}),
		}),
		GlobalReportList: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/report-spec/get-all-with-permission',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 || result?.status === 'SUCCESS',
			}),
		}),
		getWorkFlowSummary: builder.mutation({
			query: (data) => ({
				url: '/lynorg-service/endpoint/process/get-workflow-summary',
				method: 'POST',
				body: data,
				validateStatus: (response, result) => response.status === 200 || result?.status === 'SUCCESS',
			}),
		}),
		// getGeneralPDF: builder.mutation({
		// 	query: (data) => ({
		// 		url: '/lynorg-service/endpoint/process/generate-pdf-from-template',
		// 		method: 'POST',
		// 		body: data,
		// 		validateStatus: (response, result) => response.status === 200 || result?.status === 'SUCCESS',
		// 	}),
		// }),
	}),
});

export const {
	useSendControlDataMutation,
	useGlobalReportListMutation,
	useDynamicReportListMutation,
	useSearchMemberMutation,
	useGetRecentConversationMutation,
	useGetPageListMutation,
	useGetInboxCardListMutation,
	useGetIdentityInboxCardListMutation,
	useGetUserHistoryMutation,
	useSaveFormMutation,
	useSaveJSFileMutation,
	useGetFormMutation,
	useGetDelegationCardListMutation,
	useGetImageListByUserIdsMutation,
	useGetImageByIdQuery,
	useGetProfilePicQuery,
	useGetOnetoOneChatCountsMutation,
	useGetTaskConversationUserListMutation,
	useGetCascadeConversationUserListMutation,
	useMarkSeenMessageCascadeMutation,
	useMarkSeenMessageCascadeV3Mutation,
	useMarkSeenMessageWorkflowMutation,
	useMarkSeenMessageOneToOneMutation,
	useGetLeaseConsoleDataMutation,
	useGetWorkFlowSummaryMutation,
	// useGetGeneralPDFMutation,
} = apiSlice;
